import { timeIntervals } from 'components/Panel/PanelEditorSidebar/PanelDesign/PanelDesignOptions';
import { useCallback, useMemo, useState } from 'react';
import { TimeDurationTunableProps } from '../types';

export function useTimeDurationTunable({
    tag,
    onFormSubmit,
}: Pick<TimeDurationTunableProps, 'onFormSubmit' | 'tag'>): {
    handleCancel: () => void;
    handleConfirm: () => void;
    handleUpdateDuration: (event: any, index: number) => void;
    removeItem: (index: number) => void;
    handleUpdateTimeInterval: (event: any, index: number) => void;
    handleAddItem: () => void;
    durationText: string;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    formState: any[];
} {
    const blank = useMemo(
        () => ({
            value: null,
            interval: null,
        }),
        []
    );

    const getFormStateFromDuration = useCallback((duration: Duration) => {
        return timeIntervals
            .slice()
            .reverse()
            .reduce((prev, curr) => {
                const intervalKey = curr.value;
                const resolved = duration?.[intervalKey];
                return resolved ? [...prev, { value: resolved, interval: intervalKey }] : prev;
            }, []);
    }, []);

    const startFormState = useMemo(() => {
        const start = getFormStateFromDuration(tag.value as Duration);
        return start.length ? start : [blank];
    }, [blank, getFormStateFromDuration, tag.value]);

    const [formState, setFormState] = useState(startFormState);
    const [isOpen, setIsOpen] = useState(false);

    const durationText = useMemo(
        () => startFormState.reduce((prev, curr) => `${prev} ${curr.value} ${curr.interval}`, ''),
        [startFormState]
    );

    const handleAddItem = useCallback(() => {
        setFormState(prev => [...prev, blank]);
    }, [blank]);

    const handleUpdateTimeInterval = useCallback((event, index) => {
        setFormState(prev => {
            const newValue = {
                value: prev?.[index].value,
                interval: event.value,
            };
            return [...prev.slice(0, index), newValue, ...prev.slice(index + 1)];
        });
    }, []);
    const removeItem = useCallback(index => {
        setFormState(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
    }, []);

    const handleUpdateDuration = useCallback((event, index) => {
        setFormState(prev => {
            const newValue = {
                value: Number(event.target.value),
                interval: prev?.[index]?.interval,
            };
            return [...prev.slice(0, index), newValue, ...prev.slice(index + 1)];
        });
    }, []);

    const formStateIsValid = useMemo(() => {
        return formState.every(item => item.interval && item.value !== null);
    }, [formState]);

    const handleConfirm = useCallback(() => {
        if (formStateIsValid) {
            const duration: number = formState.reduce(
                (prev, curr) => ({ ...prev, [curr.interval]: curr.value }),
                []
            );
            onFormSubmit?.(duration);
        }
        setIsOpen(prev => !prev);
    }, [formStateIsValid, onFormSubmit, formState]);

    const handleCancel = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    return {
        handleCancel,
        handleConfirm,
        handleUpdateDuration,
        removeItem,
        handleUpdateTimeInterval,
        handleAddItem,
        durationText,
        isOpen,
        setIsOpen,
        formState,
    };
}
